import { BASE_URL } from "@/constants/endpoints";
import { createAxiosWithAuth } from "@/utils/axios-config";
import { objectToQueryStringWithArray } from "@/utils/query-string";
import { AxiosInstance } from "axios";

let ApiPromise: Promise<AxiosInstance> | null = null;

const getApi = async (): Promise<AxiosInstance | null> => {
  if (ApiPromise) return ApiPromise;
  ApiPromise = createAxiosWithAuth(BASE_URL, "jwt");
  return ApiPromise;
};

export interface GetVoleoRequestsProps {
  CuentaOrigen?: number;
  Estados?: number[];
  TiposServicio?: number[];
  FechaDesde?: string;
  FechaHasta?: string;
  "P.Pagina"?: number;
  "P.Records"?: number;
}

export interface VoleoRequestListItem {
  id: number;
  titularCuenta: string;
  tipoServicio: string;
  establecimiento: string;
  hectareas: number;
  estado: string;
}

export interface VoleoRequestsListResponse {
  listado: VoleoRequestListItem[];
  totalPages: number;
  totalRecords: number;
}

export const getVoleoRequests = async (
  props: GetVoleoRequestsProps = {},
): Promise<VoleoRequestsListResponse | null> => {
  const api = await getApi();
  if (!api) return null;

  const queryString = objectToQueryStringWithArray(props);
  const url = queryString
    ? `/SolicitudesVoleo?${queryString}`
    : "/SolicitudesVoleo";
  const response = await api.get(url);
  return response.data;
};

export interface GetVoleoRequestPriceProps {
  TipoServicio: number;
  Modalidad: number;
  DosisKgHa: number;
}

export interface VoleoRequestPrice {
  valorUnitario: number;
}

export const getVoleoRequestPrice = async (
  props: GetVoleoRequestPriceProps,
): Promise<VoleoRequestPrice | null> => {
  const api = await getApi();
  if (!api) return null;

  const response = await api.get(
    `/SolicitudesVoleo/precio?${objectToQueryStringWithArray(props)}`,
  );
  return response.data;
};

export interface CreateVoleoRequestBody {
  cuentaOrigen: number;
  usuarioMobile?: number;
  idEstablecimiento: number;
  tipoServicio: number;
  modalidad: number;
  idGranoProductivo: number;
  idProducto?: number;
  productoNombreLibre?: string;
  dosisKgHa: number;
  hectareas: number;
  telefonoContacto: string;
  fechaDeseada: string;
  observaciones?: string;
}

export const createVoleoRequest = async (
  body: CreateVoleoRequestBody,
): Promise<string | null> => {
  const api = await getApi();
  if (!api) return null;

  const response = await api.post("/SolicitudesVoleo", body);
  return response.data;
};

export interface GetVoleoArchivoUploadUrlBody {
  tipoArchivo: string;
  nombreDocumento?: string;
}

export interface VoleoArchivoUploadUrlResponse {
  fileId: string;
  uploadUrl: string;
  fileUrl: string;
  idArchivoAdjunto: number;
}

export const getVoleoArchivoUploadUrl = async (
  id: number,
  body: GetVoleoArchivoUploadUrlBody,
): Promise<VoleoArchivoUploadUrlResponse | null> => {
  const api = await getApi();
  if (!api) return null;

  const response = await api.post(
    `/SolicitudesVoleo/${id}/archivo/upload-url`,
    body,
  );
  return response.data;
};

export interface PutVoleoArchivoUploadUrlBody {
  idArchivoAdjunto: number;
  tipoArchivo: string;
  nombreDocumento?: string;
}

export const putVoleoArchivoUploadUrl = async (
  id: number,
  body: PutVoleoArchivoUploadUrlBody,
): Promise<VoleoArchivoUploadUrlResponse | null> => {
  const api = await getApi();
  if (!api) return null;

  const response = await api.put(
    `/SolicitudesVoleo/${id}/archivo/upload-url`,
    body,
  );
  return response.data;
};

export interface SolicitudVoleoDetail {
  id: number;
  cuentaOrigen: number;
  titularCuenta: string;
  sucursal: string;
  establecimiento: string;
  tipoServicio: string;
  modalidad: string;
  tipoCultivo: string;
  producto: string;
  dosisKgHa: number;
  hectareas: number;
  cantidadTotalKg: number;
  valorUnitario: number;
  valorTotal: number;
  telefonoContacto: string;
  fechaDeseada: string;
  observaciones?: string;
  estado: string;
  hectareasEjecutadas?: number | null;
  fechaRealRealizacion?: string | null;
  motivoRechazo?: string | null;
  usuarioRechazo?: string | null;
  fechaRechazo?: string | null;
  idArchivoAdjunto?: number | null;
  nombreArchivo?: string | null;
  urlArchivo?: string | null;
  archivoDisponible?: boolean;
}

export const getVoleoRequestDetail = async (
  id: number,
): Promise<SolicitudVoleoDetail | null> => {
  const api = await getApi();
  if (!api) return null;

  const response = await api.get(`/SolicitudesVoleo/${id}`);
  return response.data;
};

export interface UpdateVoleoRequestBody {
  id: number;
  idEstado: number;
  motivoRechazo?: string;
  fechaRealRealizacion?: string;
  hectareasEjecutadas?: number;
}

export const updateVoleoRequest = async (body: UpdateVoleoRequestBody) => {
  const api = await getApi();
  if (!api) return null;

  const response = await api.patch(`/SolicitudesVoleo/${body.id}`, body);
  return response.data;
};
